import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TaskItem from './TaskItem';
import SkeletonLoader from './SkeletonLoader';

const TaskList = ({ tasks, projects, onToggleComplete, onEditTask, onDeleteTask, onArchiveTask, filter, tags, isLoading = false }) => {
  const { t } = useTranslation();

  const getFilterTitle = () => {
    switch (filter) {
      case 'completed':
        return t('tasks.completedTasks');
      case 'pending':
        return t('tasks.pendingTasks');
      case 'high':
        return t('tasks.highPriorityTasks');
      case 'medium':
        return t('tasks.mediumPriorityTasks');
      case 'low':
        return t('tasks.lowPriorityTasks');
      default:
        return t('tasks.allTasks');
    }
  };

  const getProjectForTask = (taskProjectId) => {
    return projects.find(p => p.id === taskProjectId);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{getFilterTitle()}</h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('tasks.tasksFound', { count: tasks.length })}
        </p>
      </div>

      {isLoading ? (
        <SkeletonLoader variant="task" count={5} />
      ) : tasks.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl p-8 shadow-soft border border-slate-200/60 dark:border-slate-700/60">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">{t('tasks.noTasksFound')}</h3>
            <p className="text-slate-600 dark:text-slate-400">
              {filter === 'all'
                ? t('tasks.noTasksYet')
                : t('tasks.noFilteredTasks')}
            </p>
          </div>
        </motion.div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {tasks.map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <TaskItem
                  task={task}
                  project={getProjectForTask(task.projectId)}
                  onToggleComplete={onToggleComplete}
                  onEdit={onEditTask}
                  onDelete={onDeleteTask}
                  onArchive={onArchiveTask}
                  tags={tags}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default TaskList;